import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { object, string, array } from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { ReactComponent as DropdownArrow } from '../../assets/images/DropdownArrow.svg';
import useNewDocumentation from '../../Hooks/NewDocumentation/useNewDocumentation';
import CommentEditor from './CommentEditor';
import { SwalToast } from '../Common';
import DialogTransition from '../Common/Transitions/DialogTransition';

const SaveChartandTableInDocumentationModal = ({
  type,
  open,
  onClose,
  data,
  id,
}) => {
  const { fetchDocumentList, fetchPlaceholderList, saveChart, saveTable } =
    useNewDocumentation();
  const { project } = useSelector((state) => state);
  const [documentList, setDocumentList] = useState([]);
  const [placeholderList, setPlaceholderList] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [clearContentWithReset, setClearContetWithReset] = useState(false);
  const validationSchema = object().shape({
    document: object().when('addSelection', {
      is: (value) => {
        return value === '1' || value === '3';
      },
      then: () => {
        return object().shape({
          document_name: string().required('Required '),
          id: string().required('Required'),
        });
      },
      otherwise: (schema) => {
        return schema.notRequired();
      },
    }),
    placeholder: object().when('addSelection', {
      is: (value) => {
        return value === '1' || value === '3';
      },
      then: () => {
        return object().shape({
          id: string().required('Required'),
          placeholder_name: string().required('Required '),
        });
      },
      otherwise: (schema) => {
        return schema.notRequired();
      },
    }),
    heading: string()
      .required('Required')
      .max(250, 'Maximum 250 characters are allowed.'),
    tag: array().when('addSelection', {
      is: (value) => value === '2' || value === '3',
      then: array().min(1, 'At least one tag is required').required('Required'),
      otherwise: array().notRequired(),
    }),
  });
  const formikForm = useFormik({
    initialValues: {
      document: {
        id: '',
        document_name: '',
      },
      placeholder: {
        id: '',
        placeholder_name: '',
      },
      heading: '',
      description: '',
      tag: [],
      addSelection: '1',
    },
    validationSchema,
    onSubmit: (values) => {
      let payload;
      let tagType;
      if (type === 'chart') {
        tagType = 'Chart';
      } else if (type === 'image') {
        tagType = 'Image';
      } else if (type === 'table') {
        tagType = 'Table';
      } else tagType = 'Testcase';

      if (values.addSelection === '1') {
        payload = {
          document_id: values.document?.id,
          placeholder_id: values.placeholder.id,
          data,
          heading: values.heading,
          description: values.description,
          tag_value_id: id,
        };
      } else if (values.addSelection === '2') {
        payload = {
          data,
          heading: values.heading,
          description: values.description,
          tag_names: values.tag,
          project_id: project?.activeProject?.project_id,
          tag_value_id: id,
          tag_type: tagType,
        };
      } else if (values.addSelection === '3') {
        payload = {
          tag_names: values.tag,
          data,
          heading: values.heading,
          description: values.description,
          document_id: values.document?.id,
          project_id: project?.activeProject?.project_id,
          placeholder_id: values.placeholder.id,
          tag_value_id: id,
          tag_type: tagType,
        };
      }

      if (type === 'chart' || type === 'image') {
        saveChart(payload, Number(formikForm.values.addSelection)).then(
          (res) => {
            SwalToast({
              icon: 'success',
              title: res?.data?.msg,
            });
          }
        );
      } else {
        saveTable(payload, Number(formikForm.values.addSelection)).then(
          (res) => {
            SwalToast({
              icon: 'success',
              title: res?.data?.msg,
            });
          }
        );
      }
      onClose();
    },
  });

  const handleReset = useCallback(() => {
    formikForm.resetForm();
    setClearContetWithReset(true);
  }, [formikForm]);

  const handleSubmit = useCallback(() => {
    formikForm.handleSubmit();
  }, [formikForm]);

  useEffect(() => {
    if (
      formikForm.values.addSelection === '1' ||
      formikForm.values.addSelection === '3'
    ) {
      fetchDocumentList(project?.activeProject?.project_id).then((res) => {
        setDocumentList(res.data);
      });
    }
  }, [formikForm.values.addSelection]);

  useEffect(() => {
    if (formikForm.values.document?.id) {
      fetchPlaceholderList(formikForm.values.document?.id).then((resp) => {
        setPlaceholderList(resp?.data);
      });
    }
  }, [formikForm.values.document?.id]);

  const handleAddSelection = useCallback(
    (event) => {
      formikForm.setFieldValue('addSelection', event.target.value);
    },
    [formikForm.values.addSelection]
  );

  const renderDocumentInput = useCallback(
    function (params) {
      return (
        <TextField
          required
          {...params}
          label="Document"
          name="document"
          placeholder="Select document"
          InputLabelProps={{
            shrink: true,
          }}
          onBlur={formikForm.handleBlur}
          helperText={
            formikForm?.errors?.document?.id && formikForm?.touched?.document
              ? formikForm.errors.document?.id
              : null
          }
          error={
            Boolean(formikForm?.errors?.document?.id) &&
            formikForm?.touched?.document
          }
          aria-required="true"
        />
      );
    },
    [formikForm]
  );

  const renderDocumentOptions = useCallback(
    (props, option) => (
      <Box {...props} key={option?.id}>
        {option?.document_name}
      </Box>
    ),
    []
  );

  const documentEqualToValue = useCallback(
    (option, val) => option?.id && option?.id === val?.id,
    []
  );

  const documentOnChange = useCallback(
    (event, value) => {
      formikForm.setFieldValue('document', value);
    },
    [formikForm.values.document]
  );
  const getOptionLabelDocument = useCallback(
    (option) => (option ? option.document_name : ''),
    []
  );

  const renderPlaceholderInput = useCallback(
    function (params) {
      return (
        <TextField
          required
          {...params}
          label="Placeholder"
          name="placeholder"
          placeholder="Select placeholder"
          InputLabelProps={{
            shrink: true,
          }}
          onBlur={formikForm.handleBlur}
          helperText={
            formikForm?.errors?.placeholder?.id &&
            formikForm?.touched?.placeholder
              ? formikForm.errors.placeholder?.id
              : null
          }
          error={
            Boolean(formikForm?.errors?.placeholder?.id) &&
            formikForm?.touched?.placeholder
          }
          aria-required="true"
        />
      );
    },
    [formikForm]
  );

  const renderPlaceholderOptions = useCallback(
    (props, option) => (
      <Box {...props} key={option?.id}>
        {option?.placeholder_name}
      </Box>
    ),
    []
  );

  const placeholderEqualToValue = useCallback(
    (option, val) => option?.id && option?.id === val?.id,
    []
  );

  const placeholderOnChange = useCallback(
    (event, value) => {
      formikForm.setFieldValue('placeholder', value);
    },
    [formikForm.values.document]
  );
  const getOptionLabelPlaceholder = useCallback(
    (option) => (option ? option.placeholder_name : ''),
    []
  );

  const handleCommentChange = useCallback(
    (value) => {
      formikForm.setFieldValue('description', value);
    },
    [formikForm.values.description]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      TransitionComponent={DialogTransition}
      aria-labelledby="comments-dialog-title"
    >
      <DialogTitle id="comments-dialog-title">Comments / Insights</DialogTitle>
      <IconButton
        className="mui-dialog-close-icon-btn"
        color="primary"
        aria-label="Close"
        title="Close"
        onClick={onClose}
        size="large"
        sx={{
          position: 'absolute',
          right: 16,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Grid item pb={2} xs={12}>
          <Typography
            variant="body4"
            tabIndex={0}
            role="note"
            aria-label="Important note: Fields marked with an asterisk are mandatory."
            sx={{
              fontStyle: 'normal',
              border: 'none',
              '&:focus': {
                border: '2px solid',
                borderColor: 'primary.main',
              },
            }}
          >
            <strong>Note:</strong>&nbsp;
            <i>Fields marked with * are mandatory.</i>
          </Typography>
        </Grid>
        <RadioGroup
          value={formikForm.values.addSelection}
          onChange={handleAddSelection}
          sx={{
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <FormControlLabel
            control={<Radio />}
            value="1"
            label={<Typography variant="body2">Add to document</Typography>}
          />
          <FormControlLabel
            control={<Radio />}
            value="2"
            label={<Typography variant="body2">Tag result</Typography>}
          />
          <FormControlLabel
            control={<Radio />}
            label={
              <Typography variant="body2">
                Add to document & tag result
              </Typography>
            }
            value="3"
          />
        </RadioGroup>
        <Grid container spacing={2} mt={1}>
          {formikForm.values.addSelection === '1' ||
          formikForm.values.addSelection === '3' ? (
            <Grid item xs={formikForm.values.addSelection === '1' ? 12 : 6}>
              <Autocomplete
                id="document-autocomplete"
                name="document"
                options={documentList}
                value={formikForm.values.document}
                popupIcon={<DropdownArrow />}
                disableClearable={['', undefined].includes(
                  formikForm?.values?.document?.id
                )}
                noOptionsText="No document available."
                renderInput={renderDocumentInput}
                renderOption={renderDocumentOptions}
                onChange={documentOnChange}
                isOptionEqualToValue={documentEqualToValue}
                getOptionLabel={getOptionLabelDocument}
              />
            </Grid>
          ) : null}
          {formikForm.values.addSelection === '1' ||
          formikForm.values.addSelection === '3' ? (
            <Grid item xs={formikForm.values.addSelection === '1' ? 12 : 6}>
              <Autocomplete
                id="placeholder-autocomplete"
                name="placeholder"
                options={placeholderList}
                value={formikForm.values.placeholder}
                popupIcon={<DropdownArrow />}
                disableClearable={['', undefined].includes(
                  formikForm?.values?.placeholder?.id
                )}
                noOptionsText="No placeholder available."
                renderInput={renderPlaceholderInput}
                renderOption={renderPlaceholderOptions}
                onChange={placeholderOnChange}
                isOptionEqualToValue={placeholderEqualToValue}
                getOptionLabel={getOptionLabelPlaceholder}
              />
            </Grid>
          ) : null}
          {formikForm.values.addSelection === '2' ||
          formikForm.values.addSelection === '3' ? (
            <Grid item xs={formikForm.values.addSelection === '2' ? 12 : 6}>
              <Autocomplete
                clearIcon={false}
                options={[]}
                freeSolo
                multiple
                limitTags={formikForm.values.addSelection === '2' ? 3 : 1}
                inputValue={inputValue}
                onInputChange={(e, newInputValue) => {
                  setInputValue(newInputValue);
                }}
                renderTags={(value, props) =>
                  value.map((option, index) => (
                    <Chip
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      label={option}
                      {...props({ index })}
                      color="primary"
                      size="small"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    required
                    label="Tags"
                    placeholder={
                      formikForm.values.tag.length < 1 ? 'Enter tags' : null
                    }
                    {...params}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={Boolean(
                      formikForm.errors.tag && formikForm.touched.tag
                        ? formikForm.errors.tag
                        : null
                    )}
                    helperText={
                      formikForm?.errors?.tag && formikForm?.touched?.tag
                        ? formikForm.errors.tag
                        : null
                    }
                    aria-required="true"
                  />
                )}
                value={formikForm.values.tag}
                onChange={(e, val) => {
                  formikForm.setFieldValue('tag', val);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ' || e.key === ',') {
                    e.preventDefault();
                    const trimmedInput = inputValue.trim();
                    if (
                      trimmedInput &&
                      !formikForm.values.tag.includes(trimmedInput)
                    ) {
                      formikForm.setFieldValue('tag', [
                        ...formikForm.values.tag,
                        trimmedInput,
                      ]);
                      setInputValue(''); // Clear the input value after adding the chip
                    }
                  }
                }}
                onBlur={() => {
                  const trimmedInput = inputValue.trim();
                  if (
                    trimmedInput &&
                    !formikForm.values.tag.includes(trimmedInput)
                  ) {
                    formikForm.setFieldValue('tag', [
                      ...formikForm.values.tag,
                      trimmedInput,
                    ]);
                    setInputValue('');
                  }
                }}
              />
            </Grid>
          ) : null}
          <Grid item xs={formikForm.values.addSelection === '3' ? 6 : 12}>
            <TextField
              label="Heading"
              required
              name="heading"
              placeholder="Enter heading"
              {...formikForm.getFieldProps('heading')}
              error={Boolean(
                formikForm.errors.heading && formikForm.touched.heading
                  ? formikForm.errors.heading
                  : null
              )}
              helperText={
                formikForm?.errors?.heading && formikForm?.touched?.heading
                  ? formikForm.errors.heading
                  : null
              }
              aria-required="true"
            />
          </Grid>
          <Grid item xs={12} height="42vh">
            <InputLabel
              sx={{
                paddingTop: '4px',
                paddingBottom: '8px',
              }}
            >
              Comment
            </InputLabel>
            <CommentEditor
              height="29vh"
              content={formikForm?.values?.description}
              setContent={handleCommentChange}
              clearContentWithReset={clearContentWithReset}
              setClearContetWithReset={setClearContetWithReset}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={handleReset}
          title="Reset"
          aria-label="Reset"
        >
          Reset
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          title="Submit"
          aria-label="Submit"
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SaveChartandTableInDocumentationModal.defaultProps = {
  type: 'chart',
};

SaveChartandTableInDocumentationModal.propTypes = {
  type: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.oneOfType([PropTypes.object]).isRequired,
  id: PropTypes.string.isRequired,
};

export default SaveChartandTableInDocumentationModal;
